/**
 * ============================================================
 * queryKeys.ts — Fabrica de Chaves do React Query
 * ============================================================
 *
 * Responsabilidade: Centralizar as chaves de cache do React Query
 *   para garantir consistencia entre queries e invalidacoes.
 *   Evita strings hardcoded espalhadas pelo codigo.
 *
 * Quando usar: Em todo useQuery/useMutation e invalidateQueries.
 *   Importe `queryKeys` e chame a funcao correspondente.
 *
 * @example
 *   useQuery({ queryKey: queryKeys.deals(projectId), ... });
 *   queryClient.invalidateQueries({ queryKey: queryKeys.deals(projectId) });
 * ============================================================
 */
/**
 * Normaliza "sem projeto" para undefined.
 *
 * As telas guardam activeProjectId como `string | null` e algumas chaves só
 * aceitavam `string | undefined`. Aceitar os dois sem normalizar produziria
 * DUAS chaves diferentes para o mesmo estado (['deals', null] e
 * ['deals', undefined]) — cache duplicado e invalidação que não pega.
 */
const id = (v?: string | null) => v ?? undefined;

export const queryKeys = {
  // CRM
  deals: (projectId?: string | null) => ['deals', id(projectId)] as const,
  deal: (dealId: string) => ['deal', dealId] as const,
  clients: (projectId?: string | null) => ['clients', id(projectId)] as const,
  appointments: (projectId?: string | null) => ['appointments', id(projectId)] as const,
  availabilityOverrides: (userId?: string | null) => ['availability-overrides', id(userId)] as const,
  memberActivity: (workspaceId?: string | null) => ['member-activity', id(workspaceId)] as const,

  // Tasks
  tasks: (projectId?: string | null) => ['tasks', id(projectId)] as const,
  task: (taskId: string) => ['task', taskId] as const,
  taskActivities: (taskId: string) => ['task-activities', taskId] as const,
  taskTemplates: (projectId?: string) => ['task-templates', projectId] as const,
  
  // Epics & Wiki
  epics: (projectId?: string) => ['epics', projectId] as const,
  wiki: (projectId?: string) => ['wiki', projectId] as const,

  // Team & Workspace
  profile: () => ['profile'] as const,
  workspaces: () => ['workspaces'] as const,
  projectMembers: (projectId?: string) => ['project-members', projectId] as const,
  workspaceMembers: (workspaceId?: string) => ['workspace-members', workspaceId] as const,
  teamMembers: (projectId?: string) => ['team-members', projectId] as const,

  // Settings & Config
  userSettings: (projectId?: string, userId?: string) => ['user-settings', projectId, userId] as const,
  apiTokens: (projectId?: string) => ['api-tokens', projectId] as const,

  // Funnel
  funnels: (projectId?: string) => ['funnels', projectId] as const,
  funnelEvents: (funnelId?: string) => ['funnel-events', funnelId] as const,
  funnelPages: (funnelId?: string) => ['funnel-pages', funnelId] as const,

  // Analytics
  auditLogs: (workspaceId?: string) => ['audit-logs', workspaceId] as const,
  dashboardMetrics: (projectId?: string) => ['dashboard-metrics', projectId] as const,
};
